import { listen, send } from '@actual-app/core/platform/client/connection';
import type { ServerEvents } from '@actual-app/core/types/server-events';
import type { QueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { accountGroupQueries } from './account-groups';
import { accountQueries } from './accounts';
import { resetSync, sync } from './app/appSlice';
import { categoryQueries } from './budget';
import {
  closeAndDownloadBudget,
  closeBudget,
  downloadBudget,
  loadAllFiles,
  uploadBudget,
} from './budgetfiles/budgetfilesSlice';
import {
  datosDelBarco,
  quitarCopiaDelBarco,
} from './components/pareja/fueraDelBarco';
import { pushModal } from './modals/modalsSlice';
import { addNotification } from './notifications/notificationsSlice';
import type { Notification } from './notifications/notificationsSlice';
import { payeeQueries } from './payees';
import { loadPrefs, recargarRecuerdos } from './prefs/prefsSlice';
import { URL_AYUDA } from './presupuesto';
import type { AppStore } from './redux/store';
import { signOut } from './users/usersSlice';
import { CORREO_CONTACTO } from './util/error';

// Notifications for sync events that can fire during budget load
const syncNotifications: Partial<
  Record<ServerEvents['sync-event']['type'], () => Notification>
> = {
  'deferred-messages': () => ({
    id: 'deferred-messages',
    type: 'message',
    // Sticky: firing during budget load, it would otherwise
    // auto-dismiss before the user can read it
    sticky: true,
    title: t('Update available'),
    message: t(
      'Some synced changes were made with a newer version of the app. ' +
        'They will show up here once you update this app.',
    ),
  }),
  'read-only': () => ({
    id: 'read-only',
    type: 'message',
    title: t('You can only view this budget'),
    message: t(
      'Your change was not saved. Ask the owner of this budget if you need to edit it.',
    ),
  }),
  'dropped-messages': () => ({
    id: 'dropped-messages',
    type: 'warning',
    sticky: true,
    title: t('Some changes could not be applied'),
    message: t(
      'Some changes made on another device could not be applied ' +
        'on this device, so your devices may show different values.',
    ),
  }),
};

export function listenForSyncEvent(store: AppStore, queryClient: QueryClient) {
  // TODO: Should this run on mobile too?
  const unlistenUnauthorized = listen('sync-event', async ({ type }) => {
    if (type === 'unauthorized') {
      store.dispatch(
        addNotification({
          notification: {
            type: 'warning',
            message: 'Unable to authenticate with server',
            sticky: true,
            id: 'auth-issue',
          },
        }),
      );
    }
  });

  let attemptedSyncRepair = false;
  // Rumbo: presupuestos que ya se intentaron traer solos de la nube en esta
  // sesión (una sola vez: si la nube vuelve a quejarse, elige la persona).
  const traidasDeLaNube = new Map<string, 'trayendo' | 'hecho'>();

  const unlistenSuccess = listen('sync-event', event => {
    const prefs = store.getState().prefs.local;
    const hasBudget = Boolean(prefs && prefs.id);

    const makeNotification = syncNotifications[event.type];
    if (makeNotification) {
      // Accept these while a budget is loaded OR still loading (prefs
      // arrive only after the load completes); with neither, the user
      // has left the budget and the notification would lack context
      if (hasBudget || store.getState().app.loadingText != null) {
        store.dispatch(addNotification({ notification: makeNotification() }));
      }
      return;
    }

    if (!hasBudget) {
      // Do nothing if no budget is loaded
      return;
    }

    if (event.type === 'success' || event.type === 'applied') {
      if (attemptedSyncRepair) {
        attemptedSyncRepair = false;

        store.dispatch(
          addNotification({
            notification: {
              title: t('Syncing has been fixed!'),
              message: t('Happy budgeting!'),
              type: 'message',
            },
          }),
        );
      }

      const tables = event.tables;

      if (tables.includes('prefs')) {
        void store.dispatch(loadPrefs());
      }

      // Rumbo · Travesías: las prefs sincronizadas viajan como tabla
      // `preferences` (no `prefs`), así que la botella que marca la pareja
      // no llegaba hasta recargar. Solo se releen las claves `recuerdo:*` y
      // solo se fusionan las que cambian: ni `loadPrefs` entero (idioma,
      // formatos, todas las pantallas que leen prefs) ni bucles con las
      // escrituras propias. `success` trae solo lo recibido del servidor (o
      // lo descartado en «Solo ver»); `applied` salta con cada escritura.
      if (event.type === 'success' && tables.includes('preferences')) {
        void store.dispatch(recargarRecuerdos());
      }

      if (
        tables.includes('categories') ||
        tables.includes('category_groups') ||
        tables.includes('category_mapping')
      ) {
        void queryClient.invalidateQueries({
          queryKey: categoryQueries.lists(),
        });
      }

      if (
        // Sync on accounts change because so that transfer payees are updated
        tables.includes('accounts') ||
        tables.includes('payees') ||
        tables.includes('payee_mapping')
      ) {
        void queryClient.invalidateQueries({
          queryKey: payeeQueries.lists(),
        });
      }

      if (tables.includes('accounts')) {
        void queryClient.invalidateQueries({
          queryKey: accountQueries.lists(),
        });
      }

      if (tables.includes('account_groups')) {
        void queryClient.invalidateQueries({
          queryKey: accountGroupQueries.lists(),
        });
      }
    } else if (event.type === 'error') {
      let notif: Notification | null = null;
      const learnMore = `[${t('Learn more')}](${URL_AYUDA})`;

      switch (event.subtype) {
        case 'out-of-sync':
          if (attemptedSyncRepair) {
            notif = {
              title: t('Your data is still out of sync'),
              message:
                t(
                  'We were unable to repair your sync state, sorry! You need to reset your sync state.',
                ) +
                ' ' +
                learnMore,
              sticky: true,
              id: 'reset-sync',
              button: {
                title: t('Reset sync'),
                action: () => {
                  void store.dispatch(resetSync());
                },
              },
            };
          } else {
            // A bug happened during the sync process. Sync state needs
            // to be reset.
            notif = {
              title: t('Your data is out of sync'),
              message:
                t(
                  'There was a problem syncing your data. We can try to repair your sync state to fix it.',
                ) +
                ' ' +
                learnMore,
              type: 'warning',
              sticky: true,
              id: 'repair-sync',
              button: {
                title: t('Repair'),
                action: async () => {
                  attemptedSyncRepair = true;
                  await send('sync-repair');
                  void store.dispatch(sync());
                },
              },
            };
          }
          break;

        case 'file-old-version':
          // Tell the user something is wrong with the key state on
          // the server and the key needs to be recreated
          notif = {
            title: t('Actual has updated the syncing format'),
            message: t(
              'This happens rarely (if ever again). The internal syncing format ' +
                'has changed and you need to reset sync. This will upload data from ' +
                'this device and revert all other devices.' +
                '\n\n' +
                'Old encryption keys are not migrated. If using encryption, [reset encryption here](#makeKey).',
            ),
            messageActions: {
              makeKey: () =>
                store.dispatch(
                  pushModal({
                    modal: { name: 'create-encryption-key', options: {} },
                  }),
                ),
            },
            sticky: true,
            id: 'old-file',
            button: {
              title: t('Reset sync'),
              action: () => {
                void store.dispatch(resetSync());
              },
            },
          };
          break;

        case 'file-key-mismatch':
          // Tell the user something is wrong with the key state on
          // the server and the key needs to be recreated
          notif = {
            title: t('Your encryption key need to be reset'),
            message:
              t(
                'Something went wrong when registering your encryption key id. ' +
                  'You need to recreate your key. ',
              ) + learnMore,
            sticky: true,
            id: 'invalid-key-state',
            button: {
              title: t('Reset key'),
              action: () => {
                store.dispatch(
                  pushModal({
                    modal: { name: 'create-encryption-key', options: {} },
                  }),
                );
              },
            },
          };

          break;

        case 'file-not-found':
          notif = {
            title: t('This file is not a cloud file'),
            message:
              t(
                'You need to register it to take advantage ' +
                  'of syncing which allows you to use it across devices and never worry ' +
                  'about losing your data.',
              ) +
              ' ' +
              learnMore,
            type: 'warning',
            sticky: true,
            id: 'register-file',
            button: {
              title: t('Register'),
              action: async () => {
                await store.dispatch(uploadBudget({}));
                void store.dispatch(sync());
                void store.dispatch(loadPrefs());
              },
            },
          };
          break;

        case 'file-needs-upload':
          notif = {
            title: t('File needs upload'),
            message:
              t(
                'Something went wrong when creating this cloud file. You need ' +
                  'to upload this file to fix it.',
              ) +
              ' ' +
              learnMore,
            sticky: true,
            id: 'upload-file',
            button: {
              title: t('Upload'),
              action: () => {
                void store.dispatch(resetSync());
              },
            },
          };
          break;

        case 'file-has-reset':
        case 'file-has-new-key': {
          // These two cases happen when the current group or key on
          // the server does not match the local one: the budget was
          // uploaded again whole (al guardarlo en el cofre, al cerrarlo con
          // llave o desde otro móvil) and this phone still has the old one.
          const { cloudFileId } = store.getState().prefs.local;
          if (!cloudFileId) {
            console.error(
              'Received file-has-reset or file-has-new-key error but no cloudFileId in prefs',
            );
            break;
          }
          // Rumbo: si aquí no hay nada sin subir («Solo ver», o todo subió
          // ya), traer la de la nube no pierde nada: se hace sola, una vez.
          // Si hay algo, la persona elige.
          const subtype = event.subtype;
          const estado = traidasDeLaNube.get(cloudFileId);
          if (estado === 'trayendo') {
            // Ya se está trayendo: la sincronización repite el error mientras.
            break;
          }
          void (async () => {
            if (estado === undefined) {
              traidasDeLaNube.set(cloudFileId, 'trayendo');
              let id: string | null | undefined;
              try {
                if (!(await send('sync-algo-que-perder'))) {
                  await store.dispatch(closeBudget());
                  id = await store
                    .dispatch(downloadBudget({ cloudFileId, replace: true }))
                    .unwrap();
                }
              } catch (e) {
                console.error('No se pudo traer la versión de la nube', e);
              }
              traidasDeLaNube.set(cloudFileId, 'hecho');
              if (id !== undefined) {
                // Ya se trajo (o la descarga explicó por qué no).
                if (id) {
                  store.dispatch(
                    addNotification({
                      notification: {
                        id: 'nube-traida',
                        type: 'message',
                        title: t('Your budget is up to date'),
                        message: t(
                          'A newer version of this budget was in the cloud. ' +
                            'Nothing here was waiting to be uploaded, so I ' +
                            'brought the new one.',
                        ),
                      },
                    }),
                  );
                }
                return;
              }
            }
            store.dispatch(
              addNotification({
                notification: {
                  type: 'error',
                  origen: `sync.${subtype}`,
                  ...avisoNubeCambiada(store, cloudFileId, learnMore),
                },
              }),
            );
          })();
          break;
        }
        case 'encrypt-failure':
        case 'decrypt-failure':
          if (event.meta?.isMissingKey) {
            notif = {
              title: t('Missing encryption key'),
              message: t(
                'Unable to encrypt your data because you are missing the key. ' +
                  'Create your key to sync your data.',
              ),
              sticky: true,
              id: 'encrypt-failure-missing',
              button: {
                title: t('Create key'),
                action: () => {
                  store.dispatch(
                    pushModal({
                      modal: {
                        name: 'fix-encryption-key',
                        options: {
                          onSuccess: () => store.dispatch(sync()),
                        },
                      },
                    }),
                  );
                },
              },
            };
          } else {
            notif = {
              message: t(
                'Unable to encrypt your data. You have the correct ' +
                  'key so this is likely an internal failure. To fix this, ' +
                  'reset your sync data with a new key.',
              ),
              sticky: true,
              id: 'encrypt-failure',
              button: {
                title: t('Reset key'),
                action: () => {
                  store.dispatch(
                    pushModal({
                      modal: { name: 'create-encryption-key', options: {} },
                    }),
                  );
                },
              },
            };
          }
          break;
        case 'invalid-schema':
          console.trace('invalid-schema', event.meta);
          notif = {
            title: t('Update required'),
            message: t(
              "We couldn't apply changes from the server. This probably means you " +
                'need to update the app to support the latest database.',
            ),
            type: 'warning',
          };
          break;
        case 'apply-failure':
          console.trace('apply-failure', event.meta);
          notif = {
            message: t(
              "We couldn't apply that change to the database. If it keeps happening, write to us at {{email}}.",
              { email: CORREO_CONTACTO },
            ),
          };
          break;
        case 'network':
          // Show nothing
          break;
        case 'clock-drift':
          notif = {
            title: t('Time sync issue'),
            message: t(
              'Failed to sync because your device time differs too much from the server. Please check your device time settings and ensure they are correct.',
            ),
            type: 'warning',
            sticky: true,
          };
          break;
        case 'token-expired':
          notif = null;
          void store.dispatch(signOut());
          break;
        case 'file-access-not-allowed': {
          // Rumbo: me han bajado del barco mientras lo tenía abierto. Se
          // cierra, se quita la copia de este aparato y se explica.
          notif = null;
          const estado = store.getState();
          const localId = estado.prefs.local?.id;
          if (localId) {
            const datos = datosDelBarco(
              estado.budgetfiles.allFiles ?? [],
              localId,
            );
            void (async () => {
              await store.dispatch(closeBudget());
              await quitarCopiaDelBarco(localId, datos);
              await store.dispatch(loadAllFiles());
            })();
          }
          break;
        }
        default:
          console.trace('unknown error', event);
          notif = {
            message: t(
              'There was a problem syncing your changes. Please confirm that your server is accessible.',
            ),
          };
      }

      if (notif) {
        store.dispatch(
          addNotification({
            notification: {
              type: 'error',
              origen: `sync.${event.subtype}`,
              ...notif,
            },
          }),
        );
      }
    }
  });

  return () => {
    unlistenUnauthorized();
    unlistenSuccess();
  };
}

// Rumbo: en llano. Este móvil tiene cambios sin subir y la nube tiene una
// versión nueva: la persona elige cuál se queda.
function avisoNubeCambiada(
  store: AppStore,
  cloudFileId: string,
  learnMore: string,
): Notification {
  return {
    title: t('Your budget changed in the cloud'),
    message:
      t(
        'A newer version of this budget was uploaded, from another ' +
          'phone or another window. Tap «Bring the cloud one» to ' +
          "keep going. Whatever you wrote here that hasn't been " +
          'uploaded yet will be lost. If the good one is the one on ' +
          'this phone, [upload it instead](#upload).',
      ) +
      ' ' +
      learnMore,
    messageActions: { upload: () => store.dispatch(resetSync()) },
    sticky: true,
    id: 'needs-revert',
    button: {
      title: t('Bring the cloud one'),
      action: () => {
        void store.dispatch(closeAndDownloadBudget({ cloudFileId }));
      },
    },
  };
}
